import {
  Text,
  Flex,
  Box,
  IconButton,
  Icon,
  Checkbox,
  RadioGroup,
  Stack,
  Radio,
  Image,
  useMediaQuery,
  Tooltip,
} from '@chakra-ui/react';
import polyline from '@mapbox/polyline';
import { WebMercatorViewport } from '@math.gl/web-mercator';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Layer, Popup, Source, Marker } from 'react-map-gl';
import { useParams } from 'react-router-dom';

import { IconPlay, IconPause, IconForward, IconBackward, IconStop, iconBuilding, IconQuestionMark } from 'assets/icons';
import LogoLoader from 'components/Loader/LogoLoader';
import Mapbox from 'components/Mapbox';
import EndLocationMarker from 'components/Mapbox/EndLocationMarker';
import JobPolygon from 'components/Mapbox/JobPolygon';
import JobRadius from 'components/Mapbox/JobRadius';
import StartLocationMarker from 'components/Mapbox/StartLocationMarker';
import Title from 'components/Title';
import { getBounds } from 'containers/WhoIsWorking/shared/helpers';
import GeoCoordinatesPopup from 'containers/WhoIsWorking/WhoIsWorkingMap/GeoCoordinatesPopup';
import FEATURES from 'global/features';
import { useFetchJobById } from 'hooks/jobs/useFetchJobById';
import { useFetchTimesheetById } from 'hooks/timesheet/useFetchTimesheetById';
import { useFeatureList } from 'store/useFeatureStore';
import { formatMonthDayTime, formatDistance } from 'utils/format';
import { getFullCoordinatesList, getTimelineSegments, Segment } from 'utils/mapUtils';

const TimesheetMap: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'timesheetMileage']);

  const mapRef = useRef(null);
  const allSegmentsRef = useRef<Array<Segment>>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const breadcrumbPathIndexRef = useRef<number | null>(null);

  const [cursor, setCursor] = useState<string>('auto');
  const [segments, setSegments] = useState<Segment[]>([]);

  const [mapView, setMapView] = useState<'street' | 'satellite'>('street');
  const [playState, setPlayState] = useState<boolean>(false);
  const [pauseState, setPauseState] = useState(true);
  const [showShortestRoute, setShowShortestRoute] = useState(false);
  const [showCoordinatesDot, setShowCoordinatesDot] = useState(false);
  const [activeStartLocationPopup, setActiveStartLocationPopup] = useState<any>(null);
  const [activeEndLocationPopup, setActiveEndLocationPopup] = useState<any>(null);
  const [activeGeoCoordinatePopup, setActiveGeoCoordinatePopup] = useState<any>(null);
  const [shortestRoute, setShortestRoute] = useState<any>([]);
  const [viewport, setViewport] = useState(null);

  const [isLargerThan768] = useMediaQuery(['(min-width: 768px)'], { ssr: false });

  const featureList: any = useFeatureList();

  const { id } = useParams();

  const { status, data } = useFetchTimesheetById(id!);
  const { data: jobData } = useFetchJobById(data?.payload?.job_id);

  const breadcrumbsData: Array<any> = useMemo(() => {
    const mapData = data?.payload;

    const hasCoordinates = !!mapData?.geo_locations?.length;

    if (!hasCoordinates) {
      return [];
    }

    return getFullCoordinatesList(
      mapData.geo_locations,
      mapData.clock_in.latitude,
      mapData.clock_in.longitude,
      mapData.clock_in.local_time,
      mapData.clock_out.latitude,
      mapData.clock_out.longitude,
      mapData.clock_out.local_time
    );
  }, [data]);

  useEffect(() => {
    const mapData = data?.payload;

    const hasCoordinates = !!mapData?.geo_locations?.length;

    if (!hasCoordinates) {
      allSegmentsRef.current = [];
      setSegments([]);
      return;
    }

    const coordinates = getFullCoordinatesList(
      mapData.geo_locations,
      mapData.clock_in.latitude,
      mapData.clock_in.longitude,
      mapData.clock_in.local_time,
      mapData.clock_out.latitude,
      mapData.clock_out.longitude,
      mapData.clock_out.local_time
    );

    const breaks = mapData.breaks || [];

    if (!breaks.length) {
      const segmentsData: Segment[] = [{ type: 'work', coordinates: coordinates, index: 0 }];
      allSegmentsRef.current = segmentsData;
      setSegments(segmentsData);
      return;
    }

    const allSegments = getTimelineSegments(coordinates, breaks);

    allSegmentsRef.current = allSegments;
    setSegments(allSegments);
  }, [data]);

  useEffect(() => {
    if (Array.isArray(breadcrumbsData) && breadcrumbsData.length) {
      const allCoordinates = breadcrumbsData.map((item: any) => ({
        latitude: item?.latitude,
        longitude: item?.longitude,
      }));

      // Check for emppty array
      if (!allCoordinates.length) return;

      const coordinatesBounds = getBounds(allCoordinates);

      const { longitude, latitude, zoom } = new WebMercatorViewport({
        width: window.innerWidth,
        height: Math.max(600, window.innerHeight),
      }).fitBounds(coordinatesBounds, {
        padding: isLargerThan768
          ? { left: 250, top: 130, right: 200, bottom: 130 }
          : { left: 80, top: 80, right: 80, bottom: 80 },
        offset: [0, -10],
      });

      setViewport((viewport: any) => ({
        ...viewport,
        longitude,
        latitude,
        zoom: zoom > 20 ? 20 : zoom,
        // transitionDuration: 2000,
        // transitionInterpolator: new FlyToInterpolator(),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breadcrumbsData]);

  useEffect(() => {
    const encryptedPolyline = data?.payload?.encoded_polyline;

    if (data?.payload?.encoded_polyline) {
      const decryptedPolyline = polyline.decode(encryptedPolyline);
      const changeDecryptedPolyline = decryptedPolyline.map(item => item.reverse());
      if (featureList[FEATURES.suggested_mileage]) {
        setShortestRoute(changeDecryptedPolyline);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    return () => {
      clearAnimationInterval();
    };
  }, []);

  const handleMapClick = (e: any) => {
    const coordinatesData = e.features[0]?.properties;

    if (coordinatesData && coordinatesData?.dots) {
      setActiveGeoCoordinatePopup(coordinatesData);
      setActiveStartLocationPopup(null);
      setActiveEndLocationPopup(null);
    } else {
      setActiveGeoCoordinatePopup(null);
    }
  };

  // It is bind to interactiveLayerIds
  const handleMapMouseEnter = useCallback(() => {
    setCursor('pointer');
  }, []);

  // It is bind to interactiveLayerIds
  const handleMapMouseLeave = useCallback(() => {
    setCursor('auto');
  }, []);

  const handleStartLocationClick = (userId: number, longitude: number, latitude: number) => {
    setActiveStartLocationPopup({ userId: userId, latitude: latitude, longitude: longitude });
    setActiveEndLocationPopup(null);
    setActiveGeoCoordinatePopup(null);
  };

  const handleEndLocationClick = (userId: number, longitude: number, latitude: number) => {
    setActiveEndLocationPopup({ userId: userId, latitude: latitude, longitude: longitude });
    setActiveStartLocationPopup(false);
    setActiveGeoCoordinatePopup(null);
  };

  const handleShowCoordinatesCheckboxClick = () => {
    setShowCoordinatesDot(showDot => !showDot);
    setActiveGeoCoordinatePopup(null);
  };

  const handleShowShortestRouteClick = () => {
    setShowShortestRoute(showRoute => !showRoute);
    setActiveGeoCoordinatePopup(null);
  };

  const resetAnimation = (): void => {
    // setSegments(allSegmentsRef.current);
    setPauseState(false);
    setPlayState(false);
    breadcrumbPathIndexRef.current = null;
    clearAnimationInterval();
  };

  const moveSegmentsForward = () => {
    setSegments((prev: any) => {
      let currentSegmentIndex = prev.length ? prev.length - 1 : 0;
      let currentCoordinateIndex = prev[currentSegmentIndex] ? prev[currentSegmentIndex].coordinates.length - 1 : 0;

      let currentSegment = allSegmentsRef.current[currentSegmentIndex];

      if (!currentSegment || currentCoordinateIndex + 1 >= currentSegment.coordinates.length) {
        // Move to next segment
        if (currentSegmentIndex < allSegmentsRef.current.length - 1) {
          currentSegmentIndex += 1;
          currentSegment = allSegmentsRef.current[currentSegmentIndex];
          currentCoordinateIndex = 0;
        } else {
          // Animation complete
          resetAnimation();
          return prev;
        }
      }

      const prevSegments = allSegmentsRef.current.slice(0, currentSegmentIndex);
      const newCoordinates = currentSegment.coordinates.slice(0, currentCoordinateIndex + 2);
      breadcrumbPathIndexRef.current = (breadcrumbPathIndexRef.current ?? 0) + 1;

      return [
        ...prevSegments,
        {
          coordinates: newCoordinates,
          type: currentSegment.type,
          index: currentSegment.index,
        },
      ];
    });
  };

  const startAnimation = (): void => {
    if (intervalRef.current) return;

    intervalRef.current = setInterval(() => {
      moveSegmentsForward();
    }, 250);
  };

  const clearAnimationInterval = (): void => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const handlePlayButtonClick = () => {
    breadcrumbPathIndexRef.current = 0;
    setPauseState(false);
    setPlayState(true);
    setSegments([]);
    startAnimation();
  };

  const handlePlayFromPauseState = () => {
    setPauseState(false);
    startAnimation();
  };

  const handlePauseButtonClick = () => {
    setPauseState(true);
    clearAnimationInterval();
  };

  const handleStopButtonClick = () => {
    setSegments(allSegmentsRef.current);
    resetAnimation();
  };

  const handleForwardButtonClick = () => {
    moveSegmentsForward();
  };

  const handleBackwardButtonClick = () => {
    setSegments((prev: any) => {
      let currentSegmentIndex = prev.length ? prev.length - 1 : allSegmentsRef.current.length - 1;
      let currentCoordinateIndex = prev[currentSegmentIndex] ? prev[currentSegmentIndex].coordinates.length - 1 : -1;

      let currentSegment = allSegmentsRef.current[currentSegmentIndex];

      if (!currentSegment || currentCoordinateIndex <= 0) {
        // Move to previous segment
        if (currentSegmentIndex > 0) {
          currentSegmentIndex -= 1;
          currentSegment = allSegmentsRef.current[currentSegmentIndex];
          currentCoordinateIndex = currentSegment ? currentSegment.coordinates.length - 1 : -1;
        } else {
          // Animation complete (reached beginning)
          resetAnimation();
          return prev;
        }
      }

      const prevSegments = allSegmentsRef.current.slice(0, currentSegmentIndex);
      const newCoordinates = currentSegment.coordinates.slice(0, currentCoordinateIndex);
      breadcrumbPathIndexRef.current = (breadcrumbPathIndexRef.current ?? 0) - 1;

      return [
        ...prevSegments,
        {
          coordinates: newCoordinates,
          type: currentSegment.type,
          index: currentSegment.index,
        },
      ];
    });
  };

  const handleMapViewChange = (nextValue: 'street' | 'satellite') => {
    setMapView(nextValue);
  };

  const renderMap = () => {
    if (status === 'loading') return <LogoLoader />;
    if (status === 'error')
      return (
        <Flex p="4" justifyContent="center" alignItems="center" fontWeight="600" height="50vh">
          {t('timesheetMileage:unableToFetchTimesheet')}
        </Flex>
      );

    if (viewport === null || (status === 'success' && !data?.payload?.geo_locations?.length)) {
      return (
        <Flex p="4" justifyContent="center" alignItems="center" fontWeight="600" height="50vh">
          {t('timesheetMileage:thereAreNoGPSBreadcrumbsAvailable')}
        </Flex>
      );
    }

    return (
      <>
        <Box mt="3" p="3" width="100%" height="80vh" minHeight="600px">
          <Mapbox
            initialViewState={viewport}
            style={{ width: '100%', height: '100%' }}
            mapStyle={
              mapView === 'street'
                ? 'mapbox://styles/mapbox/streets-v11'
                : 'mapbox://styles/mapbox/satellite-streets-v9'
            }
            onMouseEnter={handleMapMouseEnter}
            onMouseLeave={handleMapMouseLeave}
            onClick={handleMapClick}
            cursor={cursor}
            ref={mapRef}
            interactiveLayerIds={showCoordinatesDot ? ['dots'] : []}
          >
            {/* map style */}
            <RadioGroup
              onChange={handleMapViewChange}
              value={mapView}
              position="absolute"
              top="10px"
              left="10px"
              p="2"
              bg="white"
              colorScheme="primary"
              fontWeight="semibold"
              borderRadius="lg"
            >
              <Stack direction="row" cursor="pointer">
                <Radio size="sm" value="street" mr="2">
                  {t('common:street')}
                </Radio>
                <Radio size="sm" value="satellite">
                  {t('common:satellite')}
                </Radio>
              </Stack>
            </RadioGroup>

            {/* breadcrumbs toggler */}
            {breadcrumbsData.length >= 1 && (
              <Box
                bg="white"
                py="2"
                px="3"
                position="absolute"
                top="60px"
                left="10px"
                fontSize="sm"
                borderRadius="base"
              >
                <Checkbox
                  isChecked={showCoordinatesDot}
                  size="sm"
                  onChange={handleShowCoordinatesCheckboxClick}
                  colorScheme="primary"
                >
                  {t('common:showBreadcrumbs')}
                </Checkbox>
              </Box>
            )}

            {breadcrumbsData.length > 1 && (
              <Box
                position="absolute"
                top="90px"
                left="10px"
                ml="auto"
                mt="3"
                p="2"
                pl="0"
                bg="transparent"
                borderRadius="base"
              >
                {playState ? (
                  <>
                    <IconButton
                      onClick={handleStopButtonClick}
                      mr="2"
                      aria-label="play icon"
                      bg="secondary.500"
                      size="sm"
                    >
                      <Icon as={IconStop} />
                    </IconButton>
                    {pauseState && (
                      <IconButton
                        onClick={handleBackwardButtonClick}
                        mr="2"
                        aria-label="back icon"
                        bg="secondary.500"
                        size="sm"
                        isDisabled={segments.length === 1 && segments[0].coordinates.length === 1}
                      >
                        <Icon as={IconBackward} />
                      </IconButton>
                    )}
                    {pauseState ? (
                      <IconButton
                        onClick={handlePlayFromPauseState}
                        mr="2"
                        aria-label="play icon"
                        bg="secondary.500"
                        size="sm"
                      >
                        <Icon as={IconPlay} />
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={handlePauseButtonClick}
                        mr="2"
                        aria-label="play icon"
                        bg="secondary.500"
                        size="sm"
                      >
                        <Icon as={IconPause} />
                      </IconButton>
                    )}
                    {pauseState && (
                      <IconButton
                        onClick={handleForwardButtonClick}
                        mr="2"
                        aria-label="forward icon"
                        bg="secondary.500"
                        size="sm"
                        isDisabled={
                          segments.length === allSegmentsRef.current.length &&
                          segments[segments.length - 1].coordinates.length ===
                            allSegmentsRef.current[allSegmentsRef.current.length - 1].coordinates.length
                        }
                      >
                        <Icon as={IconForward} />
                      </IconButton>
                    )}
                  </>
                ) : (
                  <IconButton
                    onClick={handlePlayButtonClick}
                    mr="2"
                    aria-label="play icon"
                    bg="secondary.500"
                    size="sm"
                  >
                    <Icon as={IconPlay} />
                  </IconButton>
                )}
              </Box>
            )}

            {/* Job Marker */}
            {jobData?.payload?.longitude && jobData?.payload?.latitude ? (
              <Marker longitude={jobData?.payload?.longitude} latitude={jobData?.payload?.latitude} offset={[0, 0]}>
                <Image
                  boxSize="32px"
                  bg="white"
                  objectFit="cover"
                  borderRadius="50%"
                  src={iconBuilding}
                  alt="building icon"
                  p="1"
                />
              </Marker>
            ) : null}

            {data?.payload?.clock_in?.longitude && data?.payload?.clock_in?.latitude ? (
              <StartLocationMarker
                longitude={data.payload.clock_in.longitude}
                latitude={data.payload.clock_in.latitude}
                userId={data?.payload?.user_id}
                handleMarkerClick={handleStartLocationClick}
              />
            ) : null}

            {segments.length === allSegmentsRef.current.length &&
            segments[segments.length - 1]?.coordinates.length ===
              allSegmentsRef.current[allSegmentsRef.current.length - 1]?.coordinates.length &&
            data?.payload?.clock_out?.longitude &&
            data?.payload?.clock_out?.latitude ? (
              <EndLocationMarker
                longitude={data.payload.clock_out.longitude}
                latitude={data.payload.clock_out.latitude}
                userDetail={{
                  user_id: data?.payload?.user_id,
                  first_name: data?.payload?.first_name,
                  last_name: data?.payload?.last_name,
                  photo_url: data?.payload?.employee_image,
                }}
                handleMarkerClick={handleEndLocationClick}
              />
            ) : null}

            {/* Job polygon */}
            {jobData?.payload?.requireGeofence &&
            jobData?.payload?.custom_geofence &&
            Array.isArray(jobData?.payload?.geofence_coordinates) ? (
              <JobPolygon coordinates={jobData?.payload?.geofence_coordinates || []} />
            ) : null}

            {/* Job Radius */}
            {jobData?.payload?.requireGeofence &&
            !jobData?.payload.custom_geofence &&
            jobData?.payload?.longitude &&
            jobData?.payload?.latitude &&
            jobData?.payload?.radius ? (
              <JobRadius
                radius={jobData?.payload?.radius}
                latitude={jobData?.payload?.latitude}
                longitude={jobData?.payload?.longitude}
              />
            ) : null}

            {/* Show break and work segments */}
            {segments.map((segment: Segment, index: number) => (
              <Source
                key={`${segment.type}-${segment.index}-${index}`}
                id={`${segment.type}-source-${segment.index}`}
                type="geojson"
                data={{
                  type: 'Feature',
                  properties: {},
                  geometry: {
                    type: 'LineString',
                    coordinates: segment.coordinates.map(coordinate => [coordinate.longitude, coordinate.latitude]),
                  },
                }}
              >
                <Layer
                  id={`${segment.type}-layer-${segment.index}`}
                  type="line"
                  layout={{
                    'line-join': 'round',
                    'line-cap': 'round',
                  }}
                  paint={{
                    'line-color': '#6A71B1',
                    'line-width': 4,
                    ...(segment.type === 'break' && { 'line-dasharray': [1, 2] }),
                  }}
                />
              </Source>
            ))}

            {/* Shortest path */}
            {showShortestRoute ? (
              <Source
                id="shortestRoute"
                type="geojson"
                data={{
                  type: 'Feature',
                  properties: {},
                  geometry: { type: 'LineString', coordinates: shortestRoute },
                }}
              >
                <Layer
                  id="shortestRoute"
                  type="line"
                  layout={{
                    'line-join': 'round',
                    'line-cap': 'round',
                  }}
                  paint={{
                    'line-color': '#E95414',
                    'line-opacity': 0.8,
                    'line-width': 7,
                    'line-blur': 0.3,
                  }}
                />
              </Source>
            ) : null}

            {/* geo locations points */}
            {showCoordinatesDot ? (
              <Source
                id="dots"
                type="geojson"
                data={{
                  type: 'FeatureCollection',
                  // @ts-ignore
                  features: breadcrumbsData
                    .filter((coordinate: any, index: number) => {
                      // Filtering out already animated breadcrumbs
                      if (breadcrumbPathIndexRef.current !== null && index > breadcrumbPathIndexRef.current)
                        return false;

                      // filtering out start and end coordindates
                      if (
                        coordinate.longitude === data?.payload?.clock_in?.longitude &&
                        coordinate.latitude === data?.payload?.clock_in?.latitude
                      ) {
                        return false;
                      } else if (
                        coordinate.longitude === data?.payload?.clock_out?.longitude &&
                        coordinate.latitude === data?.payload?.clock_out?.latitude
                      ) {
                        return false;
                      } else {
                        return true;
                      }
                    })
                    .map((geoCoordinate: any) => {
                      return {
                        type: 'Feature',
                        properties: { ...geoCoordinate, dots: true },
                        geometry: { type: 'Point', coordinates: [geoCoordinate.longitude, geoCoordinate.latitude] },
                      };
                    }),
                }}
              >
                <Layer
                  id="dots"
                  type="circle"
                  paint={{
                    'circle-color': '#FF6720',
                    'circle-radius': 6,
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#ffffff',
                  }}
                />
              </Source>
            ) : null}

            {/* start Popup */}
            {activeStartLocationPopup && (
              <Popup
                longitude={activeStartLocationPopup?.longitude}
                latitude={activeStartLocationPopup?.latitude}
                closeButton={true}
                closeOnClick={false}
                onClose={() => setActiveStartLocationPopup(null)}
                anchor="top"
                // className="mapbox-popup-wrapper"
              >
                <Text fontSize="xs">
                  {t('common:clockedInOn')} {data?.payload?.start ? formatMonthDayTime(data?.payload?.start) : '- '}
                </Text>
              </Popup>
            )}

            {/* End Popup */}
            {activeEndLocationPopup && (
              <Popup
                longitude={activeEndLocationPopup?.longitude}
                latitude={activeEndLocationPopup?.latitude}
                closeButton={true}
                closeOnClick={false}
                onClose={() => setActiveEndLocationPopup(null)}
                anchor="top"
                // className="mapbox-popup-wrapper"
              >
                <Text fontSize="xs">
                  {data?.payload?.end ? (
                    <>
                      {t('timesheetMileage:clockedOutOn')} {formatMonthDayTime(data?.payload?.end)}
                    </>
                  ) : (
                    <>
                      {t('timesheetMileage:lastLocation')}:{' '}
                      {Array.isArray(data?.payload?.geo_locations) &&
                      data?.payload?.geo_locations.length &&
                      data?.payload?.geo_locations[data?.payload?.geo_locations.length - 1]?.location_time
                        ? formatMonthDayTime(
                            data?.payload?.geo_locations[data?.payload?.geo_locations.length - 1]?.location_time
                          )
                        : '- '}
                    </>
                  )}
                </Text>
              </Popup>
            )}

            {/* geoCoordinate point popup / Breadcrumbs */}
            {activeGeoCoordinatePopup && (
              <GeoCoordinatesPopup
                item={data?.payload}
                activeGeoCoordinatePopup={activeGeoCoordinatePopup}
                setActiveGeoCoordinatePopup={setActiveGeoCoordinatePopup}
              />
            )}
          </Mapbox>
        </Box>
      </>
    );
  };

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Title>
          {t('common:route')}
          <Box ml="3" as="span" fontSize="md" fontWeight="normal">
            {data?.payload?.first_name ? (
              <>
                <Text fontWeight="semibold" as="strong">
                  {t('common:name')}:{' '}
                </Text>
                {`${data?.payload?.first_name || ''} ${data?.payload?.last_name || ''}`}{' '}
              </>
            ) : null}
          </Box>
          <Box ml="4" as="span" fontSize="md" fontWeight="normal">
            <Text fontWeight="semibold" as="strong">
              {t('common:jobName')}:{' '}
            </Text>
            {data?.payload?.job_name}
          </Box>
          {data?.payload?.clock_in?.local_time ? (
            <Box ml="4" as="span" fontSize="md" fontWeight="normal">
              <Text fontWeight="semibold" as="strong">
                {t('common:clockIn')}:{' '}
              </Text>
              {formatMonthDayTime(data?.payload?.clock_in?.local_time)}
            </Box>
          ) : null}
          {data?.payload?.clock_out?.local_time ? (
            <Box ml="4" as="span" fontSize="md" fontWeight="normal">
              <Text fontWeight="semibold" as="strong">
                {t('common:clockOut')}:{' '}
              </Text>
              {formatMonthDayTime(data?.payload?.clock_out?.local_time)}
            </Box>
          ) : null}
          {isNaN(Number(id)) && data?.payload?.geo_locations?.length == 0 ? (
            <Tooltip
              label={t('timesheetMileage:archiveTimesheetWarningMessage', {
                timesheetId: id,
                email: 'hello@timeero.com',
              })}
              hasArrow
              fontSize="md"
              px={4}
              py={2}
              borderRadius="base"
            >
              <Box as="span" ml={2} cursor="pointer">
                <Icon as={IconQuestionMark} />
              </Box>
            </Tooltip>
          ) : null}
        </Title>
        <Box ml="3">
          {/* Shortest path toggler */}
          {shortestRoute.length ? (
            <Checkbox
              isChecked={showShortestRoute}
              size="md"
              onChange={handleShowShortestRouteClick}
              colorScheme="primary"
              mt="6px"
            >
              {t('timesheetMileage:showSuggestedRoute')}
            </Checkbox>
          ) : null}
        </Box>
      </Box>
      {showShortestRoute && (
        <Flex alignItems="center" mt={2} ml={3}>
          <Flex alignItems="center" mr={2}>
            <Box height="8px" width="8px" backgroundColor="#E95414" mr={1}></Box>
            <Text>
              {t('timesheetMileage:suggestedMileage')}:{' '}
              {!isNaN(data?.payload?.suggestedMileage) ? formatDistance(data?.payload?.suggestedMileage) : 'N/A'}
            </Text>
          </Flex>
          <Flex alignItems="center">
            <Box height="8px" width="8px" backgroundColor="#6A71B1" mr={1}></Box>
            <Text>
              {t('timesheetMileage:actualMileage')}:{' '}
              {!isNaN(data?.payload?.mileage) ? formatDistance(data?.payload?.mileage) : 'N/A'}
            </Text>
          </Flex>
        </Flex>
      )}

      {renderMap()}
    </>
  );
};

export default TimesheetMap;
